import { useMemo, useCallback } from 'react';
import { Col, Row } from 'reactstrap';

import { AcademicOfferScheduleSection } from '../../../types';
import { useAcademicOfferScheduleData } from '../loaders/AcademicOfferScheduleLoader';
import { AcademicOfferSectionScheduleTables } from './AcademicOfferSectionScheduleTables';
import { AcademicOfferCalendar } from './AcademicOfferCalendar';
import { AcademicOfferScheduleEmpty } from './AcademicOfferScheduleEmpty';

interface AcademicOfferScheduleManagerProps {
  isReadOnly?: boolean;
}

export function AcademicOfferScheduleManager({
  isReadOnly,
}: AcademicOfferScheduleManagerProps) {
  /**
   * Se usa scheduleData (NonStructuredAcademicOfferScheduleResponse) del endpoint dedicado
   * GET /academic-offers/:id/schedule, que trae sections, timeModules y activities
   * en 1 sola llamada. Esto alimenta tanto las tablas CRUD como el calendario.
   *
   * Las tablas CRUD (AcademicOfferSectionScheduleTables) sí tienen su propio loader
   * interno (NonStructuredSectionSchedulesLoader) por sección, porque el
   * formulario de edición necesita datos adicionales del endpoint
   * GET /sections/schedules/:sectionId (campuses, teachers, modalities, periodicities, etc.)
   * que no están en el endpoint de schedule de la oferta.
   */
  const { data: scheduleData, refresh } = useAcademicOfferScheduleData();

  const sections: AcademicOfferScheduleSection[] = useMemo(() => {
    return scheduleData?.sections ?? [];
  }, [scheduleData]);

  const timeModules = useMemo(() => scheduleData?.timeModules ?? [], [
    scheduleData,
  ]);

  const activities = useMemo(() => scheduleData?.activities ?? [], [
    scheduleData,
  ]);

  /**
   * Al guardar/editar/eliminar una clase en las tablas CRUD, se recarga
   * el endpoint dedicado para obtener los sectionSchedules actualizados.
   * Esto actualiza el calendario automáticamente sin necesidad de refrescar la página.
   */
  const handleScheduleSaved = useCallback(() => {
    refresh();
  }, [refresh]);

  if (sections.length === 0) {
    return <AcademicOfferScheduleEmpty />;
  }

  return (
    <Row>
      <Col xs={12}>
        {/* Tablas CRUD: cada sección carga su propio loader con datos de edición */}
        <AcademicOfferSectionScheduleTables
          sections={sections}
          onScheduleSaved={handleScheduleSaved}
          isReadOnly={isReadOnly}
        />
      </Col>

      <Col xs={12} className="mt-5">
        {/* Calendario debajo de las tablas según diseño */}
        <AcademicOfferCalendar
          sections={sections}
          modules={timeModules}
          legends={activities}
        />
      </Col>
    </Row>
  );
}
